/* eslint-disable no-console */
import { useState, useEffect } from 'react'
import { useRouter } from 'next/router'

// Custom hooks
import { useAuth } from '@/hooks/useAuth'

// Next componets
import Head from 'next/head'
import Image from 'next/image'

// MUI componets
import Container from '@material-ui/core/Container'
import Button from '@material-ui/core/Button'
import FormControl from '@material-ui/core/FormControl'
import OutlinedInput from '@material-ui/core/OutlinedInput'
import InputAdornment from '@material-ui/core/InputAdornment'
import IconButton from '@material-ui/core/IconButton'
import Grid from '@material-ui/core/Grid'
import FormControlLabel from '@material-ui/core/FormControlLabel'
import Checkbox from '@material-ui/core/Checkbox'
import Alert from '@material-ui/lab/Alert'
import userService from '@/utils/services/user'

import VisibilityOutlinedIcon from '@material-ui/icons/VisibilityOutlined'
import VisibilityOffOutlinedIcon from '@material-ui/icons/VisibilityOffOutlined'

// Organisms
import { FooterLogin } from '@/components/organisms/Layout/FooterAuth'
import { LogoHeader } from '@/components/molecules/logo-header'

// Resources
import ImgLogin from '@/public/images/login/image.jpg'
import authService from '@/utils/services/auth'

import { setCookie, parseCookies, destroyCookie } from 'nookies'
import Link from 'next/link'

// styles
import styles from './styles.module.css'
import { UserRoles } from '@/types/user/user_roles'
import useCheckDealerProfile from '@/hooks/useCheckDealerProfile'

const INITIAL_CREDENTIALS = {
  email: '',
  password: '',
}

export const Login: React.FC = () => {
  const router = useRouter()
  // const dispatch = useDispatch()
  const [credentials, setCredentials] = useState(INITIAL_CREDENTIALS)
  const [showPassword, setShowPassword] = useState(false)
  const [showAlert, setShowAlert] = useState(false)
  const [rememberMe, setRememberMeValue] = useState(false)
  const [message, setMessage] = useState('')
  const [isLoading, setIsLoading] = useState(false)

  const { checkDealerProfile } = useCheckDealerProfile()
  const { setSession } = useAuth()
  // let dealerAddressIncomplete=false

  const handleChange = (ev) => {
    setCredentials({
      ...credentials,
      [ev.target.name]: ev.target.value,
    })
  }

  const handleClickShowPassword = () => {
    setShowPassword(!showPassword)
  }

  const handleClickRememberMe = (checked) => {
    setRememberMeValue(checked)
  }

  useEffect(() => {
    if (process.browser) {
      if (window.localStorage.getItem('remember_email')) {
        setCredentials({
          ...credentials,
          email: window.localStorage.getItem('remember_email'),
        })
        setRememberMeValue(true)
      } else {
        setRememberMeValue(false)
      }
    }
  }, [])

  const submitLogin = (ev) => {
    ev.preventDefault()
    const { redirectUrl } = parseCookies(null)
    destroyCookie(null, 'redirectUrl', { path: '/' })
    setIsLoading(true)
    authService
      .login(credentials)
      .then(async ({ data }) => {
        // If login is success and "Remember me" is checked, then save the email on localStorage
        if (process.browser) {
          if (rememberMe) {
            window.localStorage.setItem('remember_email', credentials.email)
          } else {
            window.localStorage.setItem('remember_email', '')
          }
        }

        setCookie(null, 'session', data.jwt, { path: '/' })
        setCookie(null, 'user', JSON.stringify(data.user), { path: '/' })
        setSession(data.user, data.jwt)
        if (data?.user?.role_id === UserRoles.DEALER) {
          //check dealer's profile completion
          try {
            const response = await userService.getUser(data?.user?.id as string)
            checkDealerProfile(response)
          } catch (error) {
            // eslint-disable-next-line no-console
            console.log(error)
          }
        }
        router.push(redirectUrl || '/agreements')
      })
      .catch(function (error) {
        setIsLoading(false)
        if (error.response) {
          if (error.response.status === 401) {
            setMessage(error.response.data.message)
            setShowAlert(true)
          } else {
            setMessage(
              error.response.data?.message
                ? error.response.data?.message
                : 'An unknown error occurred. Please try again later.'
            )
            setShowAlert(true)
          }
        } else if (error.request) {
          // Handle cases where the request was made but no response was received
          setMessage('Unable to reach the server. Please check your network connection and try again.')
          setShowAlert(true)
        } else {
          //Handle any other errors, including CORS issues
          setMessage(
            'A login error occurred. Please ensure you have a stable internet connection and try again. If the issue persists, please contact support.'
          )
          setShowAlert(true)
        }
      })
  }

  return (
    <>
      <Head>
        <title>Signin | HCIDoculink</title>
      </Head>

      <Container maxWidth="xl" className="login-mod">
        <Grid container spacing={1}>
          <Grid item lg={6} xs={12} className="center-element form login-form-wrapper">
            <Container maxWidth="xs" className="form__login">
              <div>
                <LogoHeader className="main-logo" />
                <div>
                  <label className="head__login" id="head__login">
                    Sign in to your account
                  </label>
                </div>
                <form onSubmit={submitLogin} autoComplete="off">
                  <div>
                    <FormControl fullWidth variant="outlined">
                      <label htmlFor="email" className="label__login">
                        Email address
                      </label>
                      <OutlinedInput
                        id="email"
                        type="email"
                        name="email"
                        value={credentials.email}
                        required
                        onChange={handleChange}
                        disabled={isLoading}
                        aria-describedby="email-error"
                        classes={{ input: styles.emailInput }}
                      />
                    </FormControl>
                  </div>
                  <br />
                  <div>
                    <FormControl fullWidth variant="outlined">
                      <label htmlFor="password" className="label__login">
                        Password
                      </label>
                      <OutlinedInput
                        classes={{ input: styles.passwordInput }}
                        id="password"
                        type={showPassword ? 'text' : 'password'}
                        value={credentials.password}
                        name="password"
                        required
                        onChange={handleChange}
                        disabled={isLoading}
                        endAdornment={
                          <InputAdornment position="end">
                            <IconButton id="toggle-show-password" onClick={handleClickShowPassword}>
                              {showPassword ? <VisibilityOutlinedIcon /> : <VisibilityOffOutlinedIcon />}
                            </IconButton>
                          </InputAdornment>
                        }
                        aria-describedby="password-error"
                      />
                    </FormControl>
                  </div>
                  <br />
                  <Grid container spacing={0}>
                    <Grid item xs={6} className="remember" style={{ textAlign: 'initial' }}>
                      <FormControlLabel
                        className="remember__text"
                        id="remember__text"
                        control={
                          <Checkbox
                            checked={rememberMe}
                            id="remember__checkbox"
                            name="checkedC"
                            className="input__check"
                            disabled={isLoading}
                            onChange={(e) => {
                              handleClickRememberMe(e.target.checked)
                            }}
                          />
                        }
                        label="Remember me"
                      />
                    </Grid>
                    <Grid item xs={6} className="forgot__pwd">
                      <div className="forgot__pwd">
                        <Link href="/auth/forgot-password">
                          <a id="forgot-link" className="forgot__text">
                            Forgot your password?
                          </a>
                        </Link>
                      </div>
                    </Grid>
                  </Grid>
                  <br />
                  <Button
                    type="submit"
                    color="primary"
                    disabled={isLoading}
                    style={{
                      width: '100%',
                      textTransform: 'none',
                      color: '#FFF',
                      fontSize: '16px',
                    }}
                    variant="contained"
                    id="sign_in_login"
                    className={styles.loggingButton}
                  >
                    Sign in
                  </Button>
                </form>
              </div>
              <div className="alert__error">
                {showAlert ? (
                  <Alert
                    severity="error"
                    id="alert_login_error"
                    style={{
                      width: '100%',
                      fontFamily: 'Inter',
                      fontStyle: 'normal',
                      fontWeight: 500,
                      fontSize: '14px',
                      lineHeight: '20px',
                      zIndex: 1,
                    }}
                  >
                    {message}
                  </Alert>
                ) : (
                  <Alert severity="error" style={{ display: 'none' }}></Alert>
                )}
              </div>
            </Container>
          </Grid>
          <Grid item md={6} xs={12} style={{ padding: '20px' }} className="login-image-wrapper">
            <Image src={ImgLogin.src} width={608} height={598} alt="Placeholder" />
          </Grid>
        </Grid>
      </Container>

      <FooterLogin />

      <style jsx>{`
        :global(.main-logo) {
          margin-bottom: 24px;
        }
        :global(body) {
          background-color: #ffffff;
        }
        :global(.MuiContainer-root.login-mod) {
          min-height: calc(100vh - 96px);
          display: grid;
          place-items: center;
          align-content: center;
        }
        :global(.MuiBox-root.box__footer) {
          margin-top: 0;
        }
        .content {
          display: flex;
        }
        .card {
          display: flex;
          flex-direction: row;
          align-items: flex-start;
          padding: 0px;
          position: absolute;
          width: 100%;
          width: -moz-available;
          height: 770px;
          left: 0px;
          right: 0px;
        }
        :global(.MuiPaper-root.MuiCard-root.MuiPaper-elevation1.MuiPaper-rounded.card__login) {
          box-shadow: none;
        }
        .form_login {
          box-shadow: none !important;
          margin: 20% 0;
          display: flex;
          flex-wrap: nowrap;
          align-content: center;
          justify-content: center;
          align-items: center;
          flex-direction: column;
          width: 576px;
          height: 404px;
        }
        .head__login {
          font-family: 'Inter', 'Be Vietnam', sans-serif;
          font-style: normal;
          font-weight: 800;
          font-size: 30px;
          line-height: 36px;
          text-align: center;
          display: flex;
          margin: auto;
          margin-bottom: 30px;
          flex-direction: row;
          flex-wrap: nowrap;
          align-content: center;
          justify-content: flex-start;
          align-items: flex-start;
        }
        label.label__login {
          display: flex;
          font-family: 'Inter', 'Be Vietnam', sans-serif;
          font-style: normal;
          font-weight: 500;
          font-size: 14px;
          line-height: 20px;
          color: #374151;
          margin-bottom: 10px;
        }
        :global(.MuiInputBase-root.MuiOutlinedInput-root.MuiInputBase-formControl
            > input[type='email'].MuiOutlinedInput-input) {
          position: relative;
          width: 100%;
          height: 24px;
          left: 6.5px;
          font-family: 'Inter', 'Be Vietnam', sans-serif;
          font-style: normal;
          font-weight: normal;
          font-size: 16px !important;
          line-height: 24px;
          color: #6b7280;
          order: 0;
          margin: 0px 0px;
        }
        :global(.MuiInputBase-root.MuiOutlinedInput-root.MuiInputBase-formControl
            > input[type='password'].MuiInputBase-input.MuiOutlinedInput-input.MuiInputBase-inputAdornedEnd.MuiOutlinedInput-inputAdornedEnd) {
          position: relative;
          width: 100%;
          height: 24px;
          left: 6.5px;
          font-family: 'Inter', 'Be Vietnam', sans-serif;
          font-style: normal;
          font-weight: 400;
          font-size: 16px !important;
          line-height: 24px;
          color: #6b7280;
        }
        :global(.MuiInputBase-root.MuiOutlinedInput-root.MuiInputBase-formControl
            > input[type='text'].MuiInputBase-input.MuiOutlinedInput-input.MuiInputBase-inputAdornedEnd.MuiOutlinedInput-inputAdornedEnd) {
          position: relative;
          width: 100%;
          height: 24px;
          left: 6.5px;
          font-family: 'Inter', 'Be Vietnam', sans-serif;
          font-style: normal;
          font-weight: 400;
          font-size: 16px !important;
          line-height: 24px;
          color: #6b7280;
        }
        .remember {
          display: flex;
          flex-direction: row;
          flex-wrap: nowrap;
          align-content: center;
          justify-content: flex-start;
          align-items: center;
          text-align: initial;
        }
        .remember__text {
          font-family: 'Inter', 'Be Vietnam', sans-serif;
          font-style: normal;
          font-weight: normal;
          font-size: 14px;
          line-height: 20px;
          color: #111827;
          text-align: initial;
        }
        :global(.MuiCheckbox-root.Mui-checked) {
          color: ${rememberMe || rememberMe == null ? '#0047ba' : '#D1D5DB'};
        }
        :global(.MuiTypography-root.MuiFormControlLabel-label.MuiTypography-body1) {
          font-family: 'Inter', 'Be Vietnam', sans-serif;
          font-style: normal;
          font-weight: normal;
          font-size: 13px;
          line-height: 20px;
          color: ${rememberMe || rememberMe == null ? '#0047ba' : '#111827'};
          width: max-content;
          margin-top: -2px;
          margin-left: -4px;
        }
        :global(.MuiCheckbox-root) {
          color: #d1d5db;
        }
        .MuiCheckbox-root.input__check {
          color: rgb(209 213 219);
        }
        .forgot__pwd {
          display: flex;
          justify-content: flex-end;
          align-items: center;
          margin-top: 10px;
        }
        .forgot__text {
          font-family: 'Inter', 'Be Vietnam', sans-serif;
          font-style: normal;
          font-weight: 500;
          font-size: 13px;
          line-height: 20px;
          text-align: right;
          color: #0047ba;
          cursor: pointer;
          flex: none;
          order: 0;
          flex-grow: 0;
          margin: 0px 0px;
          text-decoration: none;
        }
        .alert__error {
          display: flex;
          flex-direction: row;
          flex-wrap: nowrap;
          align-content: center;
          justify-content: center;
          align-items: center;
          padding: 16px 0px;
          position: relative;
          height: 52px;
          top: 20px;
          max-width: 421px;
        }
        @media (min-width: 1500px) {
          :global(.MuiContainer-root.login-mod) {
            margin-top: auto;
          }
        }
        @media (max-width: 1280px) {
          :global(.login-image-wrapper) {
            display: none;
          }
          :global(.login-form-wrapper) {
            display: flex;
            flex-direction: column;
            flex-wrap: nowrap;
            align-content: center;
            justify-content: center;
            align-items: center;
            margin-top: 48px;
          }
          :global(.MuiPaper-root.MuiCard-root.MuiPaper-elevation1.MuiPaper-rounded.card__login) {
            /* height: 498px; */
            box-shadow: none;
            margin-bottom: auto;
            margin-top: 84px;
          }
        }

        @media (max-width: 480px) {
          .alert__error {
            display: flex;
            flex-wrap: nowrap;
            align-content: center;
            justify-content: center;
            align-items: center;
            padding: 0px;
            position: relative;
            height: 52px;
            top: 20px;
          }
          .card {
            display: -webkit-box;
            display: -webkit-flex;
            display: -ms-flexbox;
            display: flex;
            -webkit-flex-direction: row;
            -ms-flex-direction: row;
            flex-direction: row;
            -webkit-align-items: center;
            -webkit-box-align: center;
            -ms-flex-align: center;
            align-items: center;
            vertical-align: middle;
            -webkit-box-pack: center;
            -webkit-justify-content: center;
            -ms-flex-pack: center;
            justify-content: center;
            margin: auto;
            text-align: center;
            padding: 0px;
            position: absolute;
            left: 0;
            right: 0;
            height: auto;
            top: 50%;
            -webkit-transform: translateY(-50%);
            -ms-transform: translateY(-50%);
            transform: translateY(-50%);
          }
          :global(.MuiPaper-root.MuiCard-root.MuiPaper-elevation1.MuiPaper-rounded.card__login) {
            /* height: 498px; */
            box-shadow: none;
            margin-bottom: auto;
            margin-top: 96px;
          }
          .head__login {
            font-size: 25px;
          }
        }
      `}</style>
    </>
  )
}

export default Login
